import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  getAlerts,
  getAlertsTypes,
  getBrandsForFilter,
  getAccountsForFilter,
  getStates,
  getCitiesByState,
  deleteAlertType,
  getPendingCount,
  markAlertAsViewed,
} from 'actions/index';
import { Colors } from 'modules/theme';
import { push } from 'modules/history';
import AlertListView from './AlertListView';
import AlertTypeListView from './AlertTypeListView';

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-bottom: 0;
`;

const TabsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  margin-bottom: 16px;
  background-color: ${Colors.white};
  border: 1px solid #ecebf0;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
`;

const TabButton = styled.button`
  flex: 1;
  height: 40px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  border: none;
  border-radius: 8px;
  background-color: ${props => (props.$active ? 'rgba(49, 104, 131, 0.08)' : 'transparent')};
  color: ${props => (props.$active ? Colors.lightBlue : Colors.gray8)};
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover {
    color: ${Colors.lightBlue};
    background-color: ${props =>
      props.$active ? 'rgba(49, 104, 131, 0.12)' : 'rgba(0, 0, 0, 0.04)'};
  }
`;

class AlertListContainer extends Component {
  state = {
    tabSelected: 'alerts',
  };

  componentDidMount() {
    const { fetchAlerts, fetchBrands, fetchStates, getPendingCount } = this.props;

    fetchAlerts();
    fetchBrands();
    fetchStates();
    getPendingCount();
  }

  switchView = tabSelected => {
    this.setState(
      {
        tabSelected,
      },
      () => {
        const { fetchAlerts, fetchAlertsTypes, getPendingCount } = this.props;
        if (tabSelected === 'alerts') {
          fetchAlerts();
          getPendingCount();
        } else {
          fetchAlertsTypes();
          getPendingCount();
        }
      },
    );
  };

  render() {
    const {
      alerts,
      alertsTypes,
      alertsPages,
      alertsCurrentPage,
      alertsTypesPages,
      alertsTypesCurrentPage,
      loadingAlert,
      loadingAlertType,
      fetchAlerts,
      fetchAlertsTypes,
      fetchCities,
      fetchAccounts,
      getPendingCount,
      markAlertAsViewed,
      brands,
      accounts,
      states,
      cities,
      removeAlertType,
      role_code,
    } = this.props;

    const { tabSelected } = this.state;

    return (
      <Container>
        <Header>
          <TabsWrapper>
            <TabButton
              $active={tabSelected === 'alerts'}
              type="button"
              onClick={() => this.switchView('alerts')}
            >
              Alerts
            </TabButton>
            {['admin', 'general'].includes(role_code) && (
              <TabButton
                $active={tabSelected === 'alerts-types'}
                type="button"
                onClick={() => this.switchView('alerts-types')}
              >
                Configuration
              </TabButton>
            )}
          </TabsWrapper>
        </Header>
        {tabSelected === 'alerts' ? (
          <AlertListView
            list={alerts}
            pages={alertsPages}
            currentPage={alertsCurrentPage}
            loading={loadingAlert}
            fetchAlerts={fetchAlerts}
            fetchAccounts={fetchAccounts}
            getPendingCount={getPendingCount}
            markAlertAsViewed={markAlertAsViewed}
            brands={brands}
            accounts={accounts}
            states={states}
            cities={cities}
            fetchCities={fetchCities}
          />
        ) : (
          <AlertTypeListView
            list={alertsTypes}
            pages={alertsTypesPages}
            currentPage={alertsTypesCurrentPage}
            loading={loadingAlertType}
            fetchAlertsTypes={fetchAlertsTypes}
            removeAlertType={removeAlertType}
            onNewClick={() => push('/alert-types/create')}
          />
        )}
      </Container>
    );
  }
}

AlertListContainer.propTypes = {
  accounts: PropTypes.array,
  alerts: PropTypes.array,
  alertsCurrentPage: PropTypes.number,
  alertsPages: PropTypes.number,
  alertsTypes: PropTypes.array,
  alertsTypesCurrentPage: PropTypes.number,
  alertsTypesPages: PropTypes.number,
  brands: PropTypes.array,
  cities: PropTypes.array,
  fetchAccounts: PropTypes.func,
  fetchAlerts: PropTypes.func,
  fetchAlertsTypes: PropTypes.func,
  fetchBrands: PropTypes.func,
  fetchCities: PropTypes.func,
  fetchStates: PropTypes.func,
  getPendingCount: PropTypes.func,
  loadingAlert: PropTypes.bool,
  loadingAlertType: PropTypes.bool,
  markAlertAsViewed: PropTypes.func,
  removeAlertType: PropTypes.func,
  role_code: PropTypes.string,
  states: PropTypes.array,
};

function mapStateToProps(state) {
  return {
    role_code: state.auth.profile.role_code,
    alerts: state.alerts.list,
    alertsPages: state.alerts.pages,
    alertsCurrentPage: state.alerts.currentPage,
    loadingAlert: state.alerts.loading,
    alertsTypes: state.alertsTypes.list,
    alertsTypesPages: state.alertsTypes.pages,
    alertsTypesCurrentPage: state.alertsTypes.currentPage,
    loadingAlertType: state.alertsTypes.loading,

    brands: [{ label: 'All', value: '' }, ...state.brands.listForFilter],
    accounts: [{ label: 'All', value: '' }, ...state.accounts.listForFilter],
    states: [{ label: 'All', value: '' }, ...state.utils.states],
    cities: [{ label: 'All', value: '' }, ...state.utils.cities],
  };
}
function mapDispatchToProps(dispatch) {
  return {
    fetchAlerts: params => dispatch(getAlerts(params)),
    fetchAlertsTypes: params => dispatch(getAlertsTypes(params)),
    fetchBrands: () => dispatch(getBrandsForFilter()),
    fetchAccounts: params => dispatch(getAccountsForFilter(params)),
    fetchStates: () => dispatch(getStates()),
    fetchCities: stateId => dispatch(getCitiesByState(stateId)),
    getPendingCount: () => dispatch(getPendingCount()),
    markAlertAsViewed: params => dispatch(markAlertAsViewed(params)),
    removeAlertType: alertTypeId => dispatch(deleteAlertType(alertTypeId)),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(AlertListContainer);
